"use client";

import { ColumnDef } from "@tanstack/react-table";
import { PizzaType } from "@/api/custom-schema";
import { Checkbox } from "@/components/ui/checkbox";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SortableHeader } from "@/components/sortable_header";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@/context/localization-context";
import { translate } from "@/i18n/translate";

const PizzaTypeDetailCell = ({ row, getLink }: { row: { original: { id: string } }; getLink: (id: string) => string }) => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  return (
    <Button
      variant="outline"
      onClick={(event) => {
        event.preventDefault();
        navigate(getLink(row.original.id));
      }}
    >
      {t("common.details")}
    </Button>
  );
};

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const columns: (
  getLink: (pizzaTypeId: string) => string,
) => ColumnDef<PizzaType>[] = (getLink) => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={translate("pizzaTypes.table.selectAll")}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={translate("pizzaTypes.table.selectRow")}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "id",
    header: ({ column }) => (
      <SortableHeader column={column} label={translate("pizzaTypes.columns.id")} />
    ),
    cell: ({ row }) => (
      <div className="font-mono text-sm">{row.getValue("id")}</div>
    ),
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <SortableHeader column={column} label={translate("common.name")} />
    ),
    cell: ({ row }) => (
      <div className="font-medium">{row.getValue("name")}</div>
    ),
  },
  {
    accessorKey: "components",
    header: () => translate("pizzaTypes.columns.ingredients"),
    cell: ({ row }) => {
      const components = row.getValue("components") as PizzaType["components"];
      return (
        <div className="flex flex-wrap gap-1 max-w-sm">
          {components.map((component, index) => (
            <Badge key={index} variant="outline" className="text-xs">
              {component.name}
            </Badge>
          ))}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: "component_count",
    header: ({ column }) => (
      <SortableHeader column={column} label={translate("pizzaTypes.columns.ingredientCount")} />
    ),
    cell: ({ row }) => {
      const components = row.getValue("components") as PizzaType["components"];
      return (
        <div className="text-center">
          <Badge variant="secondary">{components.length}</Badge>
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const aComponents = rowA.getValue("components") as PizzaType["components"];
      const bComponents = rowB.getValue("components") as PizzaType["components"];
      return aComponents.length - bComponents.length;
    },
  },
  {
    id: "detail",
    header: () => translate("common.details"),
    cell: ({ row }) => {
      return <PizzaTypeDetailCell row={row} getLink={getLink} />;
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const pizzaType = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">{translate("pizzaTypes.table.openMenu")}</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{translate("common.actions")}</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(pizzaType.id)}
            >
              {translate("pizzaTypes.actions.copyId")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(pizzaType.name)}
            >
              {translate("pizzaTypes.actions.copyName")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                navigator.clipboard.writeText(JSON.stringify(pizzaType))
              }
            >
              {translate("pizzaTypes.actions.copyData")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];